import React from "react";
import getOptimizedUrl from '../utils/imageUtils';
import { calculateShippingQuoteFromSubtotal } from '../utils/shippingUtils';

const BillSidebar = ({ isOpen, onClose, order, hideDownloadButton = false }) => {
  const [mounted, setMounted] = React.useState(false);
  const [closing, setClosing] = React.useState(false);
  const receiptRef = React.useRef(null);

  // Prevent body scroll when sidebar is open
  React.useEffect(() => {
    if (isOpen) {
      document.documentElement.style.setProperty('overflow', 'hidden', 'important');
      document.body.style.setProperty('overflow', 'hidden', 'important');
    } else {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    }

    return () => {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setClosing(false);
    } else if (mounted) {
      setClosing(true);
      setTimeout(() => {
        setMounted(false);
        setClosing(false);
      }, 300);
    }
  }, [isOpen, mounted]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setMounted(false);
      setClosing(false);
      onClose();
    }, 300);
  };

  const handleDownload = async () => {
    try {
      // Dynamically import html2canvas only when needed
      const html2canvas = (await import('html2canvas')).default;

      const receiptElement = receiptRef.current;
      if (!receiptElement) return;

      // Clone the receipt element to avoid modifying the original
      const clone = receiptElement.cloneNode(true);
      const images = clone.querySelectorAll('img');
      
      // Fetch and convert product images via backend proxy
      const imagePromises = Array.from(images).map(async (img, index) => {
        const originalImg = receiptElement.querySelectorAll('img')[index];
        const originalSrc = originalImg.src;
        
        // Keep local images (like logo), proxy S3 images
        if (originalSrc.includes('amazonaws.com')) {
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/receipt/proxy-image`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ imageUrl: originalSrc }),
            });
            
            if (response.ok) {
              const { dataUrl } = await response.json();
              img.src = dataUrl;
            } else {
              console.warn('Failed to fetch image via proxy:', originalSrc);
              // Create placeholder
              const placeholder = document.createElement('div');
              placeholder.style.width = getComputedStyle(img).width;
              placeholder.style.height = getComputedStyle(img).height;
              placeholder.style.backgroundColor = '#f3f4f6';
              img.parentNode.replaceChild(placeholder, img);
            }
          } catch (err) {
            console.warn('Error fetching image:', err);
            // Create placeholder on error
            const placeholder = document.createElement('div');
            placeholder.style.width = getComputedStyle(img).width;
            placeholder.style.height = getComputedStyle(img).height;
            placeholder.style.backgroundColor = '#f3f4f6';
            img.parentNode.replaceChild(placeholder, img);
          }
        }
      });
      
      await Promise.all(imagePromises);
      
      // Add padding to the clone for better spacing in downloaded receipt
      const receiptContent = clone.querySelector('.max-w-sm');
      if (receiptContent) {
        receiptContent.classList.add('px-6');
      }
      
      // Temporarily append clone to document (required for html2canvas)
      clone.style.position = 'absolute';
      clone.style.left = '-9999px';
      document.body.appendChild(clone);
      
      // Wait a bit for rendering
      await new Promise(resolve => setTimeout(resolve, 100));

      // Capture the cloned receipt as canvas
      const canvas = await html2canvas(clone, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
      });
      
      // Remove the clone
      document.body.removeChild(clone);

      // Convert to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `receipt-${order._id.slice(-6).toUpperCase()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
    } catch (error) {
      console.error('Failed to download receipt:', error);
      alert('Failed to download receipt. Please try again.');
    }
  };

  if (!mounted || !order) return null;

  // Exclude cancelled/returned items from receipt. Rejected exchanges remain billable.
  const activeItems = order.items?.filter((item) =>
    item.cancelledStatus !== 'cancelled' &&
    (!item.returnStatus || item.returnStatus === 'none' || item.returnStatus === 'rejected') &&
    (!item.exchangeStatus || item.exchangeStatus === 'none' || item.exchangeStatus === 'rejected')
  ) || [];

  const pricedItems = activeItems.map((item) => {
    const quantity = Number(item.quantity || 0);
    const discountedUnitPrice = Number(item.discountedPrice ?? item.price ?? 0);
    const originalUnitPrice = Number(
      item.originalPrice ?? item.unitMrp ?? item.mrp ?? discountedUnitPrice
    );
    const safeOriginalUnitPrice =
      Number.isFinite(originalUnitPrice) && originalUnitPrice > 0
        ? originalUnitPrice
        : discountedUnitPrice;

    const lineOriginalAmount = safeOriginalUnitPrice * quantity;
    const lineDiscountedAmount = discountedUnitPrice * quantity;
    const lineItemDiscount = Math.max(0, lineOriginalAmount - lineDiscountedAmount);

    return {
      ...item,
      quantity,
      discountedUnitPrice,
      originalUnitPrice: safeOriginalUnitPrice,
      lineOriginalAmount,
      lineDiscountedAmount,
      lineItemDiscount,
    };
  });

  // Recalculate totals excluding cancelled items
  const recalculatedItemsPrice = pricedItems.reduce((sum, item) => sum + item.lineDiscountedAmount, 0);
  const computedItemDiscountTotal = pricedItems.reduce((sum, item) => sum + item.lineItemDiscount, 0);
  const persistedItemDiscountTotal = Number(order.itemDiscountTotal || 0);
  const itemDiscountTotal = Math.max(computedItemDiscountTotal, persistedItemDiscountTotal);
  const originalItemsSubtotal = recalculatedItemsPrice + itemDiscountTotal;
  const couponDiscount = Number(order.couponDiscount || 0);
  const fallbackShipping = calculateShippingQuoteFromSubtotal(recalculatedItemsPrice);
  const resolvedShippingPrice = Number.isFinite(Number(order.shippingPrice))
    ? Number(order.shippingPrice)
    : Number(fallbackShipping.shippingPrice || 0);
  const recalculatedTotalPrice =
    recalculatedItemsPrice - couponDiscount + (order.taxPrice || 0) + resolvedShippingPrice;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-[10000]"
        onClick={handleClose}
      ></div>

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-full sm:max-w-md bg-white z-[10001] shadow-2xl flex flex-col drawer-panel ${
          closing ? "drawer-slide-out" : "drawer-slide-in"
        }`}
        style={{
          transformOrigin: "right center",
        }}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 sm:p-2">
            <h2 className="text-black text-base font-bdogrotesk font-medium">
              Order Receipt
            </h2>
            <button
              onClick={handleClose}
              className="text-black text-base font-bdogrotesk font-medium"
            >
              Close
            </button>
          </div>

          {/* Receipt Content */}
          <div ref={receiptRef} className="flex-1 overflow-y-auto no-scrollbar px-4 sm:px-0 py-4 sm:py-3">
            <div
              className="w-full max-w-sm mx-auto uppercase"
              style={{
                fontFamily: "'Courier Prime', monospace",
                letterSpacing: '-0.028em',
                fontWeight: 'normal'
              }}
            >
              {/* Logo */}
              <div className="text-center mb-6">
                <img
                  src="/nufab_black.png"
                  alt="NUFAB"
                  className="h-12 mx-auto"
                  crossOrigin="anonymous"
                />
                <div className="text-base tracking-wider">
                  RECEIPT
                </div>
              </div>

              {/* Billed To & Order Info */}
              <div className="flex justify-between text-base mb-4">
                <div>
                  <div className="mb-1">BILLED TO:</div>
                  <div>ORDER NO.:</div>
                </div>
                <div className="text-right">
                  <div className="mb-1">
                    {order.shippingAddress?.name || "Customer"}
                  </div>
                  <div>#{order._id.slice(-6).toUpperCase()}</div>
                </div>
              </div>

              <div className="border-t-2 border-dashed border-black my-4"></div>

              {/* Order Items */}
              {activeItems.length > 0 ? (
                <div className="space-y-6">
                  {pricedItems.map((item, index) => (
                    <div key={index}>
                      {/* Product Layout */}
                      <div className="flex gap-3">
                        {/* Product Image */}
                        {item.image && (
                          <div className="w-36 h-48   overflow-hidden flex-shrink-0">
                            <img
                              src={getOptimizedUrl(item.image || '', 1200)}
                              alt={item.productName}
                              className="w-full h-full object-cover bg-white"
                            />
                          </div>
                        )}

                        {/* Product Details */}
                        <div className="flex-1 flex flex-col justify-between">
                          {/* Top Section: Name and Size */}
                          <div>
                            <div className="text-base">
                              {item.productName}
                            </div>
                            {item.size && (
                              <div className="text-base">{item.size}</div>
                            )}
                          </div>

                          {/* Bottom Section: Price Details */}
                          <div className="text-base">
                            <div className="flex justify-between">
                              <span>UNIT PRICE:</span>
                              <span>{item.originalUnitPrice?.toLocaleString("en-IN")}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>QTY:</span>
                              <span>{item.quantity}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>NET AMOUNT:</span>
                              <span>
                                {item.lineOriginalAmount?.toLocaleString(
                                  "en-IN"
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {index < activeItems.length - 1 && (
                        <div className="border-t-2 border-dashed border-black my-4"></div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-center py-6">
                  No billable items in this order.
                </div>
              )}

              <div className="border-t-2 border-dashed border-black my-4"></div>

              {/* Totals */}
              <div className="text-sm">
                <div className="flex justify-between">
                  <span>SUB TOTAL:</span>
                  <span>{originalItemsSubtotal?.toLocaleString("en-IN")}</span>
                </div>
                {itemDiscountTotal > 0 && (
                  <div className="flex justify-between">
                    <span>DISCOUNT:</span>
                    <span>-{itemDiscountTotal.toLocaleString("en-IN")}</span>
                  </div>
                )}
                {couponDiscount > 0 && (
                  <div className="flex justify-between">
                    <span>
                      {order.couponCode
                        ? `COUPON (${order.couponCode}):`
                        : "COUPON APPLIED:"}
                    </span>
                    <span>-{couponDiscount.toLocaleString("en-IN")}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>TAX:</span>
                  <span>{order.taxPrice && order.taxPrice > 0 ? order.taxPrice.toLocaleString("en-IN") : "INCLUDED"}</span>
                </div>
                <div className="flex justify-between">
                  <span>SHIPPING COST:</span>
                  <span>
                    {resolvedShippingPrice === 0
                      ? "Free"
                      : resolvedShippingPrice.toLocaleString("en-IN")}
                  </span>
                </div>
              </div>

              <div className="border-t-2 border-dashed border-black my-4"></div>

              {/* Grand Total */}
              <div className="text-sm">
                <div className="flex justify-between text-base mb-1">
                  <span>TOTAL:</span>
                  <span>{recalculatedTotalPrice?.toLocaleString("en-IN")}</span>
                </div>
                <div className="text-sm text-black">(Inc. of all taxes)</div>
              </div>

              <div className="border-t-2 border-dashed border-black my-4"></div>

              {/* Additional Info */}
              <div className="text-sm text-center text-black ">
                <div>Order Date: {formatDate(order.createdAt)}</div>
                <div>Payment Method: {order.paymentMethod}</div>
                {order.delhivery?.waybill && (
                  <div>Tracking: {order.delhivery.waybill}</div>
                )}
              </div>

              {/* Footer */}
              <div className="text-center text-sm text-black mt-8">
                Thanks for choosing handcraft!
              </div>
            </div>
          </div>

          {/* Action Buttons - Only show download if order is paid/placed (not pending) */}
          {!hideDownloadButton && order.orderStage && (
            <div className="px-4 py-3 sm:p-2 space-y-2">
              <button
                type="button"
                onClick={handleDownload}
                className="group w-full bg-black text-white px-3 py-3 flex items-center justify-start text-[15px] font-semibold tracking-wide box-border"
              >
                <div className="relative h-5 overflow-hidden">
                  <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                    <span className="flex font-bdogrotesk font-medium text-base h-5 items-center">
                      Download Receipt
                    </span>
                    <span className="flex h-5 font-medium text-base items-center">Download Receipt</span>
                  </div>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .fixed.right-0 * {
            visibility: visible;
          }
          .fixed.right-0 {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            box-shadow: none;
          }
        }
      `}</style>
    </>
  );
};

export default BillSidebar;
